import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MapPin, Home, Menu } from "lucide-react";
import PublicServicesMenu from '@/components/navigation/PublicServicesMenu';
import PublicFaqSupportLinks from '@/components/navigation/PublicFaqSupportLinks';
import UserMenu from './UserMenu';
import { CountrySwitcher } from './tenancy/CountrySwitcher';
import { PublicCountrySwitcher } from './tenancy/PublicCountrySwitcher';
import NotificationBell from './NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useAddressGenerationAccess } from '@/hooks/useAddressGenerationAccess';

/** Must stay aligned with `AuthContainer` portal landing routes — missing roles used to fall through to `/` (public search). */
const PORTAL_HOME_BY_ROLE: Record<string, string> = {
  admin: '/admin',
  qls_admin: '/admin',
  partner: '/partner',
  surveyor: '/surveyor',
  business: '/business',
  law_enforcement: '/law-enforcement',
  ems: '/ems',
  fire_department: '/fire-department',
  disaster_management: '/disaster-management',
  utility_management: '/utility-management',
  utility_service_owner: '/utility-management',
  utility_service_agent: '/utility-management',
  road_infrastructure: '/road-infrastructure',
  waste_management: '/waste-management',
  waste_service_owner: '/waste-management',
  waste_service_agent: '/waste-management',
  tax_revenue: '/tax-revenue',
  census_statistics: '/census-statistics',
  permit_licensing: '/permit-licensing',
  property_management: '/property-management',
  real_estate: '/real-estate',
  delivery_logistics: '/delivery-logistics',
  delivery_service_owner: '/delivery-logistics',
  delivery_service_agent: '/delivery-logistics',
  tourism_hospitality: '/tourism-hospitality',
  education_services: '/education-services',
  health_services: '/health-services',
  social_services: '/social-services',
  business_registration_admin: '/business-registration-admin/filings',
  community_mapping: '/community-mapping',
  taxpayer: '/taxpayer',
  vendor_admin: '/vendor-admin',
  user: '/user',
};

/** Normalize pathname without trailing slash (except root). */
function normalizedPath(pathname: string): string {
  if (pathname.length <= 1) return pathname || '/';
  return pathname.replace(/\/$/, '') || '/';
}

/** True when URL is under this portal root (`/portal` or `/portal/...`). */
function isPathUnderPortal(path: string, portalHome: string): boolean {
  if (path === portalHome) return true;
  return path.startsWith(`${portalHome}/`);
}

type SharedHeaderProps = {
  /** Finance/operator portals should not show public FAQ, Support, Services, or Generate Address. */
  hidePublicNav?: boolean;
};

const SharedHeader = ({ hidePublicNav = false }: SharedHeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isQlsAdmin } = useAuth();
  const { role } = useUserRole();
  const { hasAccess } = useAddressGenerationAccess();
  const path = normalizedPath(location.pathname);
  // Home/search do not require a country. Visitors stay on the country we detect.
  const isWorldwideSearchPage = path === '/' || path === '/search';
  const showVisitorCountryPicker = !user && !isWorldwideSearchPage && !hidePublicNav;
  const showPublicNav = !hidePublicNav;
  const showGenerateAddress = Boolean(user && hasAccess && showPublicNav);

  /**
   * Dashboard toggles address search ↔ portal home for the signed-in role.
   * Search lives at `/` (and `/search`). Logo always goes to `/`; Dashboard
   * must match that even on operator portals that hide public nav.
   */
  const getDashboardRoute = () => {
    // QLS Admin signs in as `qls_admin` (not `admin`). Missing that key made
    // Dashboard on search fall through to `/`, so they could not return.
    const portalHome =
      (role && PORTAL_HOME_BY_ROLE[role]) ||
      (isQlsAdmin ? '/admin' : undefined);
    const path = normalizedPath(location.pathname);

    if (isWorldwideSearchPage) {
      return portalHome ?? '/';
    }
    if (portalHome && isPathUnderPortal(path, portalHome)) {
      return '/';
    }
    return portalHome ?? '/';
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Home Link */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer">
              <img 
                src="/qls-logo-nav.webp" 
                alt="QLS Logo" 
                className="w-10 h-10 object-contain"
                width="40"
                height="40"
              />
              <span className="font-semibold text-accent hidden sm:block">
                Quantum Location Services
              </span>
            </Link>

            {user && (
              <Button variant="outline" size="sm" asChild>
                <Link to={getDashboardRoute()} className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Link>
              </Button>
            )}

            {showPublicNav ? (
              <div className="hidden md:block">
                <PublicServicesMenu />
              </div>
            ) : null}
          </div>

          {/* Desktop Navigation and User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {showPublicNav ? <PublicFaqSupportLinks /> : null}
            {showGenerateAddress && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/generate-address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Generate Address</span>
                </Link>
              </Button>
            )}

            {user && (
              <>
                <CountrySwitcher />
                {/* Non-admin users cannot switch country; still show the locked home country. */}
                {!isQlsAdmin ? <PublicCountrySwitcher /> : null}
                <NotificationBell />
                <UserMenu />
              </>
            )}

            {!user && (
              <>
                {showVisitorCountryPicker ? <PublicCountrySwitcher /> : null}
                <Button asChild>
                  <Link to="/auth">Sign In</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center space-x-1">
            {showPublicNav ? <PublicFaqSupportLinks /> : null}
            {user && <NotificationBell />}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64">
                <div className="flex flex-col space-y-4 mt-8">
                  {user && (
                    <Button variant="outline" asChild className="w-full justify-start">
                      <Link to={getDashboardRoute()} className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Home className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </Button>
                  )}

                  {showVisitorCountryPicker || (user && !isQlsAdmin) ? <PublicCountrySwitcher /> : null}

                  {showPublicNav ? (
                    <PublicServicesMenu
                      variant="list"
                      onNavigate={() => setMobileMenuOpen(false)}
                      className="pb-2 border-b"
                    />
                  ) : null}

                  {showGenerateAddress && (
                    <Button variant="outline" asChild className="w-full justify-start">
                      <Link to="/generate-address" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <MapPin className="w-4 h-4" />
                        Generate Address
                      </Link>
                    </Button>
                  )}

                  {user && (
                    <div className="pt-4 border-t space-y-3">
                      <CountrySwitcher />
                      <UserMenu />
                    </div>
                  )}

                  {!user && (
                    <Button asChild className="w-full">
                      <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default SharedHeader;
